import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./CancelBooking.css";
import { useAuth } from "../../AuthProvider/AuthContext.jsx";
import {
  ArrowLeft, Plane, Armchair, Luggage, AlertTriangle, ShieldCheck,
  Clock, CheckCircle2, Info, ChevronRight,
} from "lucide-react";

const API_ROOT = process.env.REACT_APP_API_URL;

if (!API_ROOT) {
  throw new Error("REACT_APP_API_URL is not configured");
}

const API_BASE = `${API_ROOT.replace(/\/$/, "")}/api`;
const authHeaders = (t) => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${t || localStorage.getItem("mbt_token") || ""}`,
});

const formatPrice = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
};

const formatHotelGuests = (booking) => {
  const adults = Number(booking?.adults || 0);
  const children = Number(booking?.children || 0);
  const adultCount = adults || 1;
  const parts = [`${adultCount} Adult${adultCount === 1 ? "" : "s"}`];
  if (children > 0) {
    parts.push(`${children} Child${children === 1 ? "" : "ren"}`);
  }
  return parts.join(", ");
};

function getFlightDisplayInfo(booking) {
  let legs = [];

  try {
    legs = Array.isArray(booking?.itinerary_legs)
      ? booking.itinerary_legs
      : JSON.parse(booking?.itinerary_legs || "[]");
  } catch {
    legs = [];
  }

  if (legs.length > 0) {
    const route = legs
      .map(
        (leg) =>
          `${leg.departCode || "—"} → ${leg.arriveCode || "—"}`
      )
      .join(" · ");

    const pnrs = [
      ...new Set(
        legs
          .map((leg) => leg.airlinePnr)
          .filter(Boolean)
      ),
    ];

    return {
      route,
      pnr: pnrs.join(" / ") || booking.airline_pnr || "—",
    };
  }

  return {
    route: `${booking?.depart_code || "—"} → ${booking?.arrive_code || "—"}`,
    pnr: booking?.airline_pnr || "—",
  };
}

const isHardBlockError = (message) =>
  typeof message === "string" &&
  /not allowed/i.test(message);
async function apiFetch(url, options) {
  const res = await fetch(url, options);
  if (!res.ok) {
    let detail = null;
    try {
      detail = await res.json();
    } catch {
      // response body wasn't JSON, ignore
    }
    console.error("🔴 [apiFetch] Full error detail:", JSON.stringify(detail, null, 2));
    const message =
      detail?.detail?.statusDetail?.message ||
      detail?.detail?.message ||
      detail?.message ||
      detail?.error ||
      `Supplier API error ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  const responseText = await res.text();



if (!res.ok) {
  let detail = null;

  try {
    detail = JSON.parse(responseText);
  } catch {
    // response body wasn't JSON
  }

  console.error(
    "🔴 [apiFetch] Full error detail:",
    JSON.stringify(detail, null, 2)
  );

  const message =
    detail?.detail?.statusDetail?.message ||
    detail?.detail?.message ||
    detail?.message ||
    detail?.error ||
    responseText ||
    `Supplier API error ${res.status}`;

  const err = new Error(message);
  err.status = res.status;
  err.detail = detail;
  throw err;
}

try {
  return JSON.parse(responseText);
} catch (err) {
  console.error("[apiFetch] Invalid JSON response:", responseText);
  throw new Error("Invalid JSON response from server.");
}
}

// ── Type configuration: every difference between flight/bus/hotel
// cancellation lives here so the rest of the component stays generic. ──
const TYPE_CONFIG = {
  flight: {
    label: "Flight",
    icon: <Plane size={22} />,
    iconClass: "cancel-type-icon--flight",
    fetchBooking: (id, token) => apiFetch(`${API_BASE}/flight-bookings/${id}`, { headers: authHeaders(token) }),
    fetchPreview: async (id, token, _selectedSeats, booking) => {
  return {
    charges: null,
    fee: 0,
    refund: null,
    offline: true,
    raw: {
      penaltyAvailable: false,
      hasValidPenalty: false,
    },
  };
},
    confirmCancel: (
  id,
  token,
  {
    cancellationType,
    reasonCode,
    remarks,
  }
) =>
  fetch(`${API_BASE}/flight-ticketing/cancel`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      bookingId: id,
      cancellationType,
      cancelCode: reasonCode,
      reqRemarks: remarks,
    }),
  }).then(async (r) => {
    const body = await r.json().catch(() => ({}));

    if (!r.ok || body.success !== true) {
      throw new Error(
        body.error ||
        body.errorInnerException ||
        body.errorDesc ||
        body.message ||
        "Cancellation failed"
      );
    }

    return body;
  }),
    cancellationTypes: {
  0: {
    label: "Normal Cancel",
    description:
      "Online cancellation with applicable airline penalty.",
    reasons: [
      {
        code: "015",
        label: "Cancel with applicable airline penalty",
      },
      {
        code: "005",
        label: "I already cancelled this directly with the airline",
      },
      {
        code: "001",
        label: "Other reason",
      },
    ],
  },

  1: {
    label: "Full Refund",
    description:
      "Offline cancellation. The booking will be cancelled only if full refund is applicable.",
    reasons: [
      {
        code: "016",
        label: "Flight schedule changed by airline",
      },
      {
        code: "005",
        label: "I already cancelled this directly with the airline",
      },
      {
        code: "001",
        label: "Other reason",
      },
    ],
  },

  2: {
    label: "No Show",
    description:
      "Offline cancellation for a no-show request.",
    reasons: [
      {
        code: "011",
        label: "Claim No Show Tax Refund",
      },
      {
        code: "001",
        label: "Other reason",
      },
    ],
  },
},
    summaryFields: (b) => [
      { label: "Route", value: getFlightDisplayInfo(b).route },
      { label: "Travel Date", value: formatDate(b.travel_date) },
      { label: "Airline", value: b.airline || "—" },
      { label: "Passengers", value: `${b.num_passengers || 1}` },
      { label: "Class", value: b.class_type || "Economy" },
      { label: "PNR", value: getFlightDisplayInfo(b).pnr },
    ],
    terms: [
      <>Cancellation charges are set by the <strong>airline's fare rules</strong> and vary by fare type — refundable fares incur lower charges than non-refundable ones.</>,
      <>Submitting this request sends it to the airline's <strong>cancellation queue</strong>. Final status and exact charges are confirmed shortly after and reflected in your booking.</>,
      <>Refunds, once approved, are credited to your <strong>original payment method</strong> within the airline's standard refund timeline (typically 7–14 business days).</>,
      <>If your flight was already cancelled or rescheduled by the airline, select <strong>"I already cancelled this directly with the airline"</strong> so it's processed without penalty where applicable.</>,
    ],
  },

  bus: {
    label: "Bus",
    icon: <Armchair size={22} />,
    iconClass: "cancel-type-icon--bus",
    fetchBooking: (id, token) => apiFetch(`${API_BASE}/bus-bookings/${id}`, { headers: authHeaders(token) })
      .catch(async () => {
        // bus.routes.js list endpoint doesn't expose GET /:id directly in
        // the shared router, so fall back to filtering the list endpoint.
        const all = await apiFetch(`${API_BASE}/bus-bookings`, { headers: authHeaders(token) });
        const match = all.find((b) => String(b.id) === String(id));
        if (!match) throw new Error("Booking not found");
        return match;
      }),
    fetchPreview: (id, token) =>
  apiFetch(`${API_BASE}/bus-bookings/${id}/cancellation-data`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({}),
  }).then((body) => {
    const d = body?.data || body;

    return {
      charges:
  d.totalCancellationCharges ??
  d.cancellationCharges ??
  d.cancellationCharge ??
  null,
      fee: d.totalCancellationServiceFee ?? 0,
      refund:
  d.totalRefundAmount ??
  d.refundAmount ??
  d.refund ??
  null,
      offline: d.cancellationType === "Offline",
      raw: body,
    };
  }),
    confirmCancel: (
  id,
  token,
  { bookingId, requestId }
) =>
  apiFetch(`${API_BASE}/bus-bookings/${id}/cancel`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      bookingId,
      requestId,
    }),
  }),
    reasons: [],
    summaryFields: (b) => [
      { label: "Route", value: `${b.from_city || "—"} → ${b.to_city || "—"}` },
      { label: "Travel Date", value: formatDate(b.travel_date) },
      { label: "Operator", value: b.operator || "—" },
      { label: "Seats", value: b.seat_numbers || "—" },
      { label: "Passengers", value: `${b.num_passengers || 1}` },
      { label: "Boarding Point", value: b.boarding_point || "—" },
    ],
    terms: [
  <>Cancellation charges depend on the <strong>operator's policy</strong> and how close to departure you cancel.</>,
  <>Your cancellation request will be submitted to the bus operator for processing.</>,
  <>If the operator supports online cancellation, the cancellation result and refund amount will be confirmed immediately.</>,
  <>If the operator requires offline processing, the cancellation will be processed manually and the final refund amount will be confirmed later.</>,
  <>After the cancellation is confirmed, the refund will be sent to your <strong>original payment method</strong> after admin approval.</>,
],
  },

  hotel: {
    label: "Hotel",
    icon: <Luggage size={22} />,
    iconClass: "cancel-type-icon--hotel",
    fetchBooking: (id, token) => apiFetch(`${API_BASE}/hotel-bookings/${id}`, { headers: authHeaders(token) }),
    fetchPreview: (id, token, _selectedSeats, booking) => {
      const supplierBookingId = booking?.booking_reference;
      if (!supplierBookingId) {
        return Promise.reject(new Error("Hotel supplier booking reference is missing."));
      }
      const cancelRequestId = `hotel_cancel_${Date.now()}`;
      

return apiFetch(`${API_BASE}/hotel-search/cancellation/init`, {
  method: "POST",
  headers: authHeaders(token),
  body: JSON.stringify({
    bookingId: supplierBookingId,
    requestId: cancelRequestId
  }),
}).then((body) => {

  

  const isOffline =
  String(body?.cancellationType || "").trim().toLowerCase() === "offline" ||
  /initiated|pending/i.test(
    String(body?.bookingStatus || body?.status || "")
  );

const normalizedPreview = {
  charges: isOffline
    ? null
    : (
        body.cancellationCharges ??
        body.cancellationCharge ??
        body.charges ??
        null
      ),

  fee: null,

  refund: null,

  offline: isOffline,

  requestId: body.requestId || cancelRequestId,

  raw: body,
};



return normalizedPreview;
});
    },
    confirmCancel: (id, token, { requestId }) => {
      
  if (!requestId) {
    return Promise.reject(
      new Error("Hotel cancellation request ID is missing.")
    );
  }

  return apiFetch(
    `${API_BASE}/hotel-bookings/${id}/cancellation-confirm`,
    {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({
        requestId,
      }),
    }
  ).then((body) => ({
    ...body,

    bookingStatus:
      body.bookingStatus ||
      body.supplierResponse?.bookingStatus,

    cancellationCharges:
      body.cancellationCharges ??
      body.supplierResponse?.cancellationCharges ??
      null,

    cancellationServiceFee:
      body.cancellationServiceFee ??
      body.supplierResponse?.cancellationServiceFee ??
      null,

    refundAmount:
      body.refundAmount ??
      body.supplierRefundAmount ??
      body.supplierResponse?.refundAmount ??
      null,
  }));
},
    reasons: [],
    summaryFields: (b) => [
      { label: "Hotel", value: b.hotel_name || "—" },
      { label: "Location", value: b.hotel_location || "—" },
      { label: "Check-in", value: formatDate(b.check_in) },
      { label: "Check-out", value: formatDate(b.check_out) },
      { label: "Guests", value: formatHotelGuests(b) },
      { label: "Room", value: b.room_type || "—" },
    ],
    terms: [
      <>Cancellation charges depend on the <strong>hotel's rate plan</strong> — free-cancellation rates incur no charge before the deadline, non-refundable rates may not be refundable at all.</>,
      <>If instant cancellation is enabled for this property, your booking is <strong>cancelled immediately</strong> and the refund amount below is final.</>,
      <>If not, your request is marked <strong>"Cancellation Initiated"</strong> and processed manually — you'll be notified once it's confirmed.</>,
      <>Approved refunds are credited to your <strong>original payment method</strong> as per the hotel's refund timeline.</>,
    ],
  },
};

function RefundRow({ label, icon, value, variant }) {
  const displayValue =
    typeof value === "string"
      ? value
      : value === null || value === undefined
        ? "—"
        : `₹${formatPrice(value)}`;

  return (
    <div className={`refund-row${variant ? ` refund-row--${variant}` : ""}`}>
      <span className="refund-row-label">
        {icon}{label}
      </span>

      <span className="refund-row-value">
        {displayValue}
      </span>
    </div>
  );
}

export default function CancelBooking() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { token, isLoggedIn, authLoading } = useAuth();

  const config = TYPE_CONFIG[type];

  const [booking, setBooking] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(true);
  const [bookingError, setBookingError] = useState(null);

  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [previewBlocked, setPreviewBlocked] = useState(false);
  const [cancellationType, setCancellationType] = useState(0);

const [reasonCode, setReasonCode] = useState(
  config?.cancellationTypes?.[0]?.reasons?.[0]?.code || ""
);
  const [remarks, setRemarks] = useState("");
  const [agreed, setAgreed] = useState(false);

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [result, setResult] = useState(null);
  const bookingRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !isLoggedIn) navigate("/", { replace: true });
  }, [authLoading, isLoggedIn, navigate]);

  const loadBooking = useCallback(async () => {
    if (!config || !id || !token) return;
    setLoadingBooking(true);
    setBookingError(null);
    try {
      const b = await config.fetchBooking(id, token);

      

      bookingRef.current = b;
      setBooking(b);
    } catch (err) {
      setBookingError(err.message);
    } finally {
      setLoadingBooking(false);
    }
  }, [config, id, token]);

  const loadPreview = useCallback(async () => {
  if (!config || !id || !token) return;

  setLoadingPreview(true);
  setPreviewError(null);
  setPreviewBlocked(false);

  try {
    const p = await config.fetchPreview(
      id,
      token,
      undefined,
      bookingRef.current
    );

    setPreview(p);
  } catch (err) {
    setPreviewError(err.message);
    setPreviewBlocked(
      type === "hotel" || isHardBlockError(err.message)
    );
  } finally {
    setLoadingPreview(false);
  }
}, [config, id, token, type]);

  // Auto-fetch booking + refund preview on load, as requested.
  useEffect(() => {
    if (!token || !config) return;
    loadBooking();
  }, [token, config, loadBooking]);

  useEffect(() => {
    if (!token || !config || type === "hotel") return;
    loadPreview();
  }, [token, config, type, loadPreview]);

  useEffect(() => {
    if (type !== "hotel" || !token || !config || !booking) return;
    loadPreview();
  }, [type, token, config, booking, loadPreview]);
  
  const handleConfirm = async () => {
    if (confirming) return;
    if (!agreed) return;
    setConfirming(true);
    setConfirmError(null);
    try {
      const res = await config.confirmCancel(id, token, {
        cancellationType,
        reasonCode,
        remarks,
        requestId: preview?.requestId,

        bookingId:
          booking.supplier_booking_id ||
          booking.booking_reference,
      });
      if (
  type === "flight" &&
  res.success === true
) {
  setResult({
    ...res,

    success: true,

    cancellationSubmitted:
      true,

    cancellationPending:
      true,

    penaltyAvailable:
      false,

    hasValidPenalty:
      false,

    totalCancellationCharges:
      null,

    totalRefundAmount:
      null,

    message:
      "Your cancellation request has been submitted successfully. The airline is processing the cancellation. Refund information will be available once the cancellation is confirmed.",
  });

  return;
}

setResult(res);
    } catch (err) {
      setConfirmError(err.message);
    } finally {
      setConfirming(false);
    }
  };

  // ── Unsupported type guard ──
  if (!config) {
    return (
      <div className="cancel-page">
        <div className="cancel-body">
          <div className="cancel-fatal-error">
            <div className="cancel-fatal-error-icon"><AlertTriangle size={40} /></div>
            <h2 className="cancel-fatal-error-title">Unsupported booking type</h2>
            <p className="cancel-fatal-error-text">
              We can't process a cancellation for "{type}" bookings on this page yet.
            </p>
            <button className="cancel-back-btn" onClick={() => navigate("/profile?tab=bookings")}>
              <ArrowLeft size={14} /> Back to My Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (authLoading || loadingBooking) {
    return (
      <div className="cancel-page">
        <div className="cancel-body">
          <div className="refund-loading">
            <div className="refund-spinner" />
            <p>Loading your booking…</p>
          </div>
        </div>
      </div>
    );
  }

  if (bookingError || !booking) {
    return (
      <div className="cancel-page">
        <div className="cancel-body">
          <div className="cancel-fatal-error">
            <div className="cancel-fatal-error-icon">
              <AlertTriangle size={40} />
            </div>

            <h2 className="cancel-fatal-error-title">
              We couldn't load this booking
            </h2>

            <p className="cancel-fatal-error-text">
              {bookingError || "This booking doesn't exist or you don't have access to it."}
            </p>

            <button
              className="cancel-back-btn"
              onClick={() => navigate("/profile?tab=bookings")}
            >
              <ArrowLeft size={14} /> Back to My Bookings
            </button>

          </div>
        </div>
      </div>
    );
  }


  if (booking.status === "completed") {
    return (
      <div className="cancel-page">
        <div className="cancel-body">
          <h2>
            This booking cannot be cancelled because travel is completed.
          </h2>
        </div>
      </div>
    );
  }

  const isPaymentDone = ["success", "paid", "captured", "completed"].includes(
    String(booking.payment_status || "").toLowerCase()
  );

  

  // ── Success screen ──
  // ── Success screen ──
if (result) {

  const cancellationPending =
    result.cancellationPending === true;

    const offline =
  String(result.cancellationType || "").trim().toLowerCase() === "offline" ||
  /cancel[_\s-]?initiated/i.test(
    String(result.bookingStatus || "")
  );

  const cancellationData =
  result?.cancellation?.data ||
  result?.cancellation ||
  {};

  const refund = null;



const cancellationCharges =
  cancellationPending || offline
    ? null
    : (
        result.supplierCancellationCharges ??
        result.totalCancellationCharges ??
        cancellationData.totalCancellationCharges ??
        cancellationData.cancellationCharges ??
        cancellationData.cancellationCharge ??
        preview?.charges ??
        null
      );

      const cancellationServiceFee = null;

  

    return (
      <div className="cancel-page">
        <div className="cancel-topbar">
          <div className="cancel-topbar-inner">
            <span className="cancel-topbar-title">
  Cancellation{" "}
  {cancellationPending || offline
    ? "Submitted"
    : "Confirmed"}
</span>
          </div>
        </div>
        <div className="cancel-body">
          <div className="cancel-success">
            <div className="cancel-success-icon"><CheckCircle2 size={36} /></div>
            <h2 className="cancel-success-title">
  {cancellationPending || offline
  ? "Your cancellation request has been submitted"
  : "Your booking has been cancelled"}
</h2>
            <p className="cancel-success-text">
  {cancellationPending
  ? "Your cancellation request has been submitted. The final refund amount will be confirmed after the supplier completes cancellation."
: offline
  ? "Your cancellation request has been submitted for manual processing. The final refund amount will be confirmed by the supplier before admin approval."
    : "Your booking has been cancelled successfully. Your refund is now pending admin approval and will be processed to your original payment method after approval."}
</p>
            {refund !== undefined && refund !== null && (
  <div className="cancel-success-summary">

  <div className="cancel-success-row">
    <span>Booking Reference</span>
    <strong>
      {booking.booking_reference ||
        booking.airline_pnr ||
        id}
    </strong>
  </div>

  {cancellationCharges !== null && (
    <div className="cancel-success-row">
      <span>Cancellation Charges</span>
      <strong>
        ₹{formatPrice(cancellationCharges)}
      </strong>
    </div>
  )}

  {cancellationServiceFee !== null && (
  <div className="cancel-success-row">
    <span>Service Fee</span>
    <strong>
      ₹{formatPrice(cancellationServiceFee)}
    </strong>
  </div>
)}

  {refund !== null && (
    <div className="cancel-success-row">
      <span>Refund Amount</span>
      <strong>
        ₹{formatPrice(refund)}
      </strong>
    </div>
  )}

</div>
)}
            <button className="cancel-success-btn" onClick={() =>
              navigate(
                type === "flight"
                  ? "/profile?tab=flights"
                  : type === "hotel"
                    ? "/profile?tab=hotels"
                  : "/profile?tab=bus"
              )
            }>
              Back to My Bookings <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const summaryFields = config.summaryFields(booking);
  const totalPaid = Number(
  booking.payment_amount ??
  booking.total_price ??
  booking.grand_total ??
  0
);

  return (
    <div className="cancel-page">
      <div className="cancel-topbar">
        <div className="cancel-topbar-inner">
          <button className="cancel-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={14} /> Back
          </button>
          <span className="cancel-topbar-title">Cancel {config.label} Booking</span>
        </div>
      </div>

      <div className="cancel-body">
        <h1 className="cancel-page-heading">Review before you cancel</h1>
        <p className="cancel-page-sub">
          Check the refund breakdown and terms below, then confirm to submit your cancellation.
        </p>

        {/* Booking summary */}
        <div className="cancel-summary-card">
          <div className="cancel-summary-head">
            <div className={`cancel-type-icon ${config.iconClass}`}>{config.icon}</div>
            <div>
              <div className="cancel-summary-title">
                {booking.destination ||
                  booking.hotel_name ||
                  (booking.itinerary_legs
                    ? getFlightDisplayInfo(booking).route
                    : `${booking.from_city || booking.depart_code || ""} → ${booking.to_city || booking.arrive_code || ""}`)}
              </div>
              <div className="cancel-summary-ref">
                REF {booking.booking_reference || booking.airline_pnr || id}
              </div>
            </div>
          </div>
          <div className="cancel-summary-grid">
            {summaryFields.map((f) => (
              <div className="cancel-summary-item" key={f.label}>
                <div className="cancel-summary-label">{f.label}</div>
                <div className="cancel-summary-value">{f.value}</div>
              </div>
            ))}
          </div>
          <div className="cancel-summary-paid">
            <span className="cancel-summary-paid-label">Total Paid</span>
            <span className="cancel-summary-paid-value">₹{formatPrice(totalPaid)}</span>
          </div>
        </div>

        {/* Refund preview */}
        <div className="refund-card">
          <div className="refund-card-head">
            <span className="refund-card-title"><ShieldCheck size={16} /> Refund Estimate</span>
          </div>
          {loadingPreview ? (
            <div className="refund-loading">
              <div className="refund-spinner" />
              <p>Checking applicable charges and refund…</p>
            </div>
          ) : previewError ? (
            <div className="refund-error">
              <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                {previewError}
                {previewBlocked
                  ? ""
                  : " You can still submit the cancellation — exact charges will be confirmed afterward."}
              </span>
            </div>
          ) : preview ? (
            <>
              <div className="refund-rows">
                <RefundRow
                  label="Cancellation Charges"
                  icon={<Clock size={13} style={{ marginRight: 4 }} />}
                  value={
  preview.offline
    ? "Calculated later"
    : preview.charges !== null
      ? preview.charges
      : "Calculated later"
}
                  variant="charge"
                />
                {!preview.offline && preview.fee > 0 && (
  <RefundRow
    label="Service Fee"
    icon={<Info size={13} style={{ marginRight: 4 }} />}
    value={preview.fee}
    variant="fee"
  />
)}
                <RefundRow label="Estimated Refund" icon={<CheckCircle2 size={13} style={{ marginRight: 4 }} />} value={
                  preview.offline
  ? "Calculated later"
  : isPaymentDone && preview.refund !== null
    ? preview.refund
    : "Calculated later"
                } variant="refund" />
              </div>
              {preview.offline && (
  <div className="refund-offline-note">
    <Info size={15} />
    <span>
      This booking requires manual cancellation processing.
      Cancellation charges and the final refund amount are not
      available yet and will be confirmed after supplier review.
    </span>
  </div>
)}
            </>
          ) : null}
        </div>

        {/* Terms & conditions, specific to booking type */}
        <div className="terms-card">
          <div className="terms-card-head">
            <span className="terms-card-title"><Info size={16} /> Cancellation Terms</span>
          </div>
          <div className="terms-list">
            {config.terms.map((t, i) => (
              <div className="terms-item" key={i}>
                <span className="terms-item-bullet" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Cancellation Type — flights only */}
{type === "flight" && config.cancellationTypes && (
  <>
    <div className="reason-card">
      <div className="reason-card-title">
        Cancellation Type
      </div>

      <div className="reason-options">
        {Object.entries(config.cancellationTypes).map(
          ([typeCode, cancellationConfig]) => (
            <label
              key={typeCode}
              className={`reason-option${
                cancellationType === Number(typeCode)
                  ? " selected"
                  : ""
              }`}
            >
              <input
                type="radio"
                name="cancellation-type"
                value={typeCode}
                checked={
                  cancellationType === Number(typeCode)
                }
                onChange={() => {
                  const nextType = Number(typeCode);

                  setCancellationType(nextType);

                  setReasonCode(
                    cancellationConfig.reasons?.[0]?.code || ""
                  );

                  setRemarks("");
                }}
              />

              <span className="reason-option-label">
                {cancellationConfig.label}
              </span>
            </label>
          )
        )}
      </div>

      <div className="reason-option-description">
        {config.cancellationTypes[cancellationType]?.description}
      </div>
    </div>

    <div className="reason-card">
      <div className="reason-card-title">
        Why are you cancelling?
      </div>

      <div className="reason-options">
        {(
          config.cancellationTypes[cancellationType]?.reasons || []
        ).map((r) => (
          <label
            key={r.code}
            className={`reason-option${
              reasonCode === r.code ? " selected" : ""
            }`}
          >
            <input
              type="radio"
              name="cancel-reason"
              value={r.code}
              checked={reasonCode === r.code}
              onChange={() => setReasonCode(r.code)}
            />

            <span className="reason-option-label">
              {r.label}
            </span>
          </label>
        ))}
      </div>

      {reasonCode === "001" && (
        <textarea
          className="reason-remarks"
          placeholder="Tell us briefly what happened…"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      )}
    </div>
  </>
)}

        {/* Confirmation acknowledgement */}
        <label className={`confirm-check-row${agreed ? " checked" : ""}`}>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span className="confirm-check-label">
            I understand the cancellation charges shown above and confirm I want to cancel this {config.label.toLowerCase()} booking.
            This action cannot be undone.
          </span>
        </label>

        {confirmError && (
          <div className="refund-error" style={{ borderRadius: "var(--r-lg)", marginBottom: 16 }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{confirmError}</span>
          </div>
        )}

        <div className="cancel-action-bar">
          <button className="cancel-btn cancel-btn--keep" onClick={() => navigate(-1)} disabled={confirming}>
            Keep My Booking
          </button>
          <button
            className="cancel-btn cancel-btn--confirm"
            disabled={
  !agreed ||
  confirming ||
  (type !== "flight" && loadingPreview) ||
  (type !== "flight" && previewBlocked)
}
            onClick={handleConfirm}
          >
            {confirming ? "Submitting…" : previewBlocked ? "Cannot Cancel" : "Confirm Cancellation"}
          </button>
        </div>
      </div>
    </div>
  );
}
